import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityPrintShopPage } from '@data/sanity/queries/types/shop'
import { getPreview } from '@lib/sanity/client'
import { getAllLocales, Locale } from '@lib/language'
import { useSetPrintShop } from '@lib/print/shop'
import { CustomStaticPropsContext, StaticPathWithId } from '@lib/routes'
import { getPrintShopPage, getAllPrintShopIds } from '@lib/sanity/shop'

import Modules from '@modules/modules'
import Layout from '@components/layout'
import ShopTemplateHeader from '@modules/shop/template/header'
import Collection from '@modules/shop/collection/index'

interface ShopPageProps {
  locale: Locale
  page: SanityPrintShopPage
  site: SanitySiteFragment
}

type ShopPageParams = ParsedUrlQuery & {
  id: string
}

const ShopPage = ({ page, site }: ShopPageProps) => {
  useSetPrintShop({
    slug: page.slug,
    name: page.name,
    active: page.active,
    institution: {
      id: page.institution.id,
      name: page.institution.name,
    },
  })

  return (
    <Layout page={page.template} site={site}>
      <ShopTemplateHeader
        title={page.template.title
          .replace(/{shop_name}/gi, page.name ?? '')
          .replace(/{school_name}/gi, page.institution?.name ?? '')}
        description={page.template.description}
      />

      <Collection
        products={page.products}
        featuredProductIds={page.template.featuredProductIds ?? []}
        paginationLimit={page.paginationLimit}
        collectionStrings={page.collectionStrings}
        filter={page.filter}
        sort={page.sort}
        printShop={page}
      />

      <Modules
        modules={page.template?.modules}
        collectionProducts={page.products}
      />
    </Layout>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<ShopPageParams>): Promise<
  GetStaticPropsResult<ShopPageProps>
> {
  const printShopSlug = params?.id ?? ''
  const printShopPage = await getPrintShopPage(
    locale,
    printShopSlug,
    getPreview(preview, previewData),
  )

  if (!printShopPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      locale,
      page: printShopPage.page,
      site: printShopPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<ShopPageParams>
> {
  const allShops = await getAllPrintShopIds()
  const allLocales = getAllLocales()
  const paths: StaticPathWithId[] = allShops
    .map((shop) => ({
      ...shop,
      locale: shop.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ id }) => !!id)
    .map(({ locale, id }) => ({
      params: {
        id,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ShopPage
